import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert } from "@/integrations/supabase/types";

export type PostRow = Tables<"posts">;

export function useFeedPosts(limit = 50) {
  return useQuery({
    queryKey: ["posts", "feed", limit],
    refetchInterval: 15_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posts")
        .select("*")
        .eq("visibility", "public")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as PostRow[];
    },
  });
}

/** Posts from the accounts the signed-in user follows. */
export function useFollowingPosts(userId: string | null) {
  return useQuery({
    queryKey: ["posts", "following", userId],
    refetchInterval: 15_000,
    enabled: !!userId,
    queryFn: async () => {
      const { data: follows, error: fErr } = await supabase
        .from("follows")
        .select("following_id")
        .eq("follower_id", userId!);
      if (fErr) throw fErr;
      const ids = (follows ?? []).map((f) => f.following_id);
      if (!ids.length) return [] as PostRow[];
      const { data, error } = await supabase
        .from("posts")
        .select("*")
        .in("author_id", ids)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as PostRow[];
    },
  });
}

export function useMyPosts(userId: string | null) {
  return useQuery({
    queryKey: ["posts", "mine", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posts")
        .select("*")
        .eq("author_id", userId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as PostRow[];
    },
  });
}

export function useCreatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: TablesInsert<"posts">) => {
      const { data, error } = await supabase.from("posts").insert(payload).select().single();
      if (error) throw error;
      return data as PostRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["posts"] }),
  });
}

export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("posts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["posts"] }),
  });
}

export function useLikePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ postId, userId }: { postId: string; userId: string }) => {
      // Check if user already liked this post
      const { data: existingLike, error: checkError } = await supabase
        .from("post_likes")
        .select("*")
        .eq("post_id", postId)
        .eq("user_id", userId)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingLike) {
        // Unlike: remove the like and decrement count
        const { error: deleteError } = await supabase
          .from("post_likes")
          .delete()
          .eq("post_id", postId)
          .eq("user_id", userId);

        if (deleteError) throw deleteError;

        // Decrement like count
        const { error: updateError } = await supabase.rpc("decrement_post_likes", { post_id: postId });
        if (updateError) throw updateError;

        return { liked: false };
      } else {
        // Like: add the like and increment count
        const { error: insertError } = await supabase
          .from("post_likes")
          .insert({ post_id: postId, user_id: userId });

        if (insertError) throw insertError;

        // Increment like count
        const { error: updateError } = await supabase.rpc("increment_post_likes", { post_id: postId });
        if (updateError) throw updateError;

        return { liked: true };
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["posts"] });
      qc.invalidateQueries({ queryKey: ["post-likes"] });
    },
  });
}

export function usePostLikes(postId: string | null) {
  return useQuery({
    queryKey: ["post-likes", postId],
    enabled: !!postId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("post_likes")
        .select("*")
        .eq("post_id", postId!);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useUserLikeStatus(postId: string | null, userId: string | null) {
  return useQuery({
    queryKey: ["post-like-status", postId, userId],
    enabled: !!postId && !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("post_likes")
        .select("*")
        .eq("post_id", postId!)
        .eq("user_id", userId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useUserLikeStatuses(postIds: string[], userId: string | null) {
  return useQuery({
    queryKey: ["post-like-statuses", postIds, userId],
    enabled: postIds.length > 0 && !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("post_likes")
        .select("*")
        .in("post_id", postIds)
        .eq("user_id", userId!);
      if (error) throw error;
      return data;
    },
  });
}
